import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Typography,
  Stack,
  Chip,
  Box,
  styled,
  alpha,
} from '@mui/material';
import { format } from 'date-fns';
import { Icon } from '@iconify/react';
import { IStudentTransaction, TransactionTableColumn, TransactionWithType } from 'src/types/transaction.types';
import { UserListHead } from 'src/sections/@dashboard/user';
import moment from 'moment';
import CustomPagination from '../CustomPagination';
import { getStatusColor } from 'src/utils/data';

interface TransactionsTableProps {
  data: IStudentTransaction[];
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onEdit?: (transaction: IStudentTransaction) => void;
  onDelete?: (transaction: IStudentTransaction) => void;
}

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  position: 'relative',
  minWidth: 800,
}));

const StyledTable = styled(Table)(({ theme }) => ({
  borderCollapse: 'separate',
  borderSpacing: '0 8px',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  border: 'none',
  padding: theme.spacing(2),
  backgroundColor: 'inherit',
  '&:first-of-type': {
    paddingLeft: theme.spacing(3),
  },
  '&:last-of-type': {
    paddingRight: theme.spacing(3),
  },
}));

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  border: 'none',
  padding: theme.spacing(2),
  color: theme.palette.text.secondary,
  backgroundColor: 'transparent',
  '&:first-of-type': {
    paddingLeft: theme.spacing(3),
  },
  '&:last-of-type': {
    paddingRight: theme.spacing(3),
  },
}));

const TABLE_HEAD: Array<{
  id: TransactionTableColumn;
  label: string;
  align?: 'left' | 'right' | 'center';
}> = [
  { id: 'studentId', label: 'Student' },
  { id: 'transactionType.name', label: 'Transaction Type' },
  { id: 'description', label: 'Description' },
  { id: 'createdAt', label: 'Date' },
  { id: 'amount', label: 'Amount ($D)' },
  { id: 'balanceAfter', label: 'Balance After Transaction ($D)' },
  { id: 'status', label: 'Status' },
  { id: 'delete', label: 'Delete', align: 'right' },
];

export function TransactionsTable({
  data,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onEdit,
  onDelete,
}: TransactionsTableProps) {
  const handlePageChange = (newPage: number) => {
    onPageChange(newPage);
  };

  // const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   onRowsPerPageChange(parseInt(event.target.value, 10));
  // };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    onRowsPerPageChange(newRowsPerPage);
  };

  return (
    <>
      <StyledTableContainer>
        <StyledTable>
          <UserListHead headLabel={TABLE_HEAD} />
          {/* <TableHead>
            <TableRow>
              {TABLE_HEAD.map((column) => (
                <StyledTableHeadCell key={column.id} align={column.align || 'left'}>
                  <Typography variant="subtitle2" noWrap>
                    {column.label}
                  </Typography>
                </StyledTableHeadCell>
              ))}
            </TableRow>
          </TableHead> */}
          <TableBody>
            {data.map((row) => (
              <StyledTableRow key={row.id} hover>
                {/* <StyledTableCell>
                  <Typography variant="subtitle2">{format(new Date(row?.createdat), 'MMM d, yyyy')}</Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="subtitle2">{row.transaction_type?.type || '--'}</Typography>
                </StyledTableCell> */}
                <StyledTableCell>
                  <Typography variant="subtitle1">
                    {row?.student ? `${row?.student?.user?.firstname} ${row?.student?.user?.lastname}` : '--'}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="subtitle1">{row.transaction_type?.name || '--'}</Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="subtitle1">{row.transaction_type?.description || '--'}</Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="subtitle1">{moment(row?.createdat).format('MM/DD/YYYY')}</Typography>
                </StyledTableCell>
                <StyledTableCell
                 align="center">
                  <Typography
                    variant="subtitle1"
                    color={row.transaction_type?.type === 'credit' ? 'success.main' : 'error.main'}
                  >
                    {row.transaction_type?.type === 'credit' ? '+' : '-'}
                    {Math.abs(row.amount)} $D
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography
                    variant="subtitle1"
                    color={row.balanceafter >= 0 ? 'text.primary.main' : 'error.main'}
                    sx={{
                      textAlign: 'center',
                    }}
                  >
                    {row.balanceafter} $D
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Chip
                    label={getStatusColor(row.status).label}
                    size="small"
                    sx={{
                      bgcolor: getStatusColor(row.status).bgcolor,
                      color: getStatusColor(row.status).color,
                      fontWeight: 'medium',
                      '& .MuiChip-label': {
                        px: 1,
                      },
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell align="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                    {onEdit && (
                      <IconButton
                        size="small"
                        onClick={() => onEdit(row)}
                        sx={{
                          color: 'primary.main',
                          '&:hover': {
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                          },
                        }}
                      >
                        <Icon icon="eva:edit-fill" width={20} height={20} />
                      </IconButton>
                    )}
                    {onDelete && (
                      <IconButton
                        size="small"
                        onClick={() => onDelete(row)}
                        sx={{
                          color: 'error.main',
                          '&:hover': {
                            bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                          },
                        }}
                      >
                        <Icon icon="eva:trash-2-outline" width={20} height={20} />
                      </IconButton>
                    )}
                  </Stack>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
      <CustomPagination
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[8, 10, 25]}
      />
    </>
  );
}
